import React, { useContext, useEffect, useState, useRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import Price from '../../../models/Price'
import Option from '../../../models/Option'
import { CustomerContext } from '../../../contexts/CustomerContext'
import placeholder from '../../../assets/placeholder_elementary.png'
import OptionDisplay from '../../OptionDisplay'
import PackageDisplay from '../../PackageDisplay/PackageDisplay'
import PackageDisplayV2 from '../../PackageDisplay/PackageDisplayV2'
import { ToastContainer } from 'react-toastify'
import { OrderPackage, Package } from '../../../models';
import styles from './OptionSelection.module.sass'
import Spinner from "../../Spinner";
import NavBar from "../../PackageDisplay/NavBar"
import Typography from '../../Typography'
import Pose from "../../../models/Pose";
import useQueryString from "../../../hooks/use-query-string";
import classes from "../Sittings/Sittings.module.sass";
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import routes from '../../../routes';
import usePoses from '../../../hooks/usePoses';
import useOfferCoupon from '../../../hooks/useOfferCoupon';
import useApplyOfferToCart from '../../../hooks/useApplyOfferToCart';
import useHolidayPricelist from '../../../hooks/useHolidayPricelist';
import PromoBanner from '../../PromoBanner';

const useOptions = (packageId, schoolId) => {
  const [options, setOptions] = useState([])

  useEffect(() => {
    const priceScope = Price.where({ for_school: schoolId, hasPrice: true });

    Option
      .includes(['image_types', 'prices'])
      .merge({ prices: priceScope })
      .where({ packageId })
      .order('abbreviation')
      .all()
      .then((response) => setOptions(response.data))
  }, [packageId])

  return options
}

const usePose = (favoritesIds) => {
  const [pose, setPose] = useState()
  const [loading, setLoading] = useState(true)
  const loadedRef = useRef(null)

  useEffect(() => {
    console.log('usePose effect running', { favoritesIds });
    
    if (!favoritesIds || (Array.isArray(favoritesIds) && favoritesIds.length === 0)) {
      console.log('No favoritesIds, setting loading false');
      setLoading(false)
      return;
    }
    
    const poseId = [favoritesIds].flat()[0];
    
    // Don't fetch if we've already loaded this pose
    if (loadedRef.current === poseId) {
      console.log('Already loaded this pose, skipping fetch');
      return;
    }
    
    console.log('Fetching pose with id:', poseId);
    loadedRef.current = poseId;
    setLoading(true)
    
    Pose
      .find(poseId)
      .then((response) => {
        console.log('Pose fetch success:', response.data);
        console.log('About to setPose and setLoading(false)');
        setPose(response.data);
        setLoading(false);
        console.log('Called setPose and setLoading(false)');
      })
      .catch((error) => {
        console.error('Error fetching pose:', error)
        setPose(null)
        setLoading(false)
      })
  }, [Array.isArray(favoritesIds) ? favoritesIds.join(',') : favoritesIds]) 

  console.log('usePose returning:', { pose, loading, favoritesIds });
  return { pose, loading }
}

const OptionSelectionContent = ({ background, foreground,
                                  multiPoseHandler, orderPackage = new OrderPackage(), packageId, accessCodeOnly,
                                  isElementary = true,
                                  heading = null,
                                  hideYearbookOption = false,
                                  onSkip = null,
                                  preorderPose }) => {
  const { customer } = useContext(CustomerContext);
  const [currentPackage, setCurrentPackage] = useState()
  const [selectedIcon, setSelectedIcon] = useState('packages')
  const { currentOrderPackage, currentSchool } = customer
  const allOptions = useOptions(packageId, currentSchool.id)
  // Senior upsell step (hideYearbookOption) drops the standalone $0 "yearbook" Option
  // card — the yearbook pose is now captured in its own step. Default off → the
  // elementary/undergrad flow keeps every option. Filtering here flows through to
  // both the collection memo and the old-display displayOptions below.
  const options = useMemo(() => (
    hideYearbookOption
      ? allOptions.filter((opt) => !opt.name?.toLowerCase().includes('yearbook'))
      : allOptions
  ), [allOptions, hideYearbookOption])
  const [collection, setCollection] = useState([])
  const { favorites: favoritesIds, ui } = useQueryString()
  const { pose: selectedPose, loading: poseLoading } = usePose(favoritesIds)

  console.log('Component render:', { 
    favoritesIds, 
    selectedPose, 
    poseLoading,
    hasCurrentPackage: !!currentPackage,
    selectedIcon
  });
  
  const { data: poses } = usePoses(customer?.currentStudent?.id, packageId);
  const history = useHistory();

  // Promotional offer for THIS pricelist — the same time-windowed for_landing_page engine
  // the senior flow uses, so elementary/undergrad promos work identically. studentId here
  // is the external school-scoped id (Student#studentId), which the resolver looks up.
  const { data: offer } = useOfferCoupon(customer?.currentStudent?.studentId, currentSchool.id, packageId);
  // The "Prints / Digital Files" tab routes to HolidayBuilder, which renders null when the
  // school has no `category: 'gift'` pricelist — so hide the tab instead of sending the
  // customer to a blank page. Same guard the senior chooser applies. 14 schools with a
  // picture day booked this school year currently have no gift pricelist.
  const { data: giftPricelist } = useHolidayPricelist(currentSchool.id);
  // Auto-apply ONLY where the promo UI actually renders — the new-package-builder path
  // (see the `currentPackage.useNewPackageBuilder` return below). On a legacy-display
  // (OptionDisplay) pricelist the banner + per-card discount don't show, so applying the
  // coupon there would be a silent, unexplained discount at checkout.
  useApplyOfferToCart(currentPackage?.useNewPackageBuilder ? offer : null);

  // Check if all poses are JPGs
  const allPosesAreJpg = poses && poses.length > 0 && poses.every(pose => 
    pose.imageUrl && pose.imageUrl.toLowerCase().includes('.jpg')
  );

  console.log('All poses are JPG:', allPosesAreJpg);

  useEffect(() => {
    Package
      .find(packageId)
      .then((res) => {
        setCurrentPackage(res.data)
      })
  }, [packageId])

  useEffect(() => {
    if (options.length === 0) return;

    let collection

    // NavBar's only tab now sets 'packages' (the old 'featured' tab was removed), so that is
    // the only real branch; the else just keeps `collection` defined defensively.
    if (selectedIcon === 'packages') {
      // ALL packages (non-add-on), featured-first then most expensive. Featured-only would
      // hide non-featured best-sellers (e.g. "Digital Value Pack"), so the single Packages
      // tab shows everything with the curated/featured ones leading. Priceless/empty options
      // fall out downstream (PackageDisplay renders nothing without a price).
      collection = options.filter((opt) => !opt.code).sort((a, b) => {
        if (!!a.featured !== !!b.featured) return a.featured ? -1 : 1
        return (b.prices?.[0]?.price || 0) - (a.prices?.[0]?.price || 0)
      })
    } else {
      collection = []
    }

    // Filter collection if all poses are JPGs
    if (allPosesAreJpg) {
      collection = collection.filter(option => 
        option.name && (
          option.name.includes('8x10') || 
          option.name.includes('Team Picture')
        )
      );
      console.log('Filtered collection for JPG-only:', collection);
    }

    setCollection(collection)
  }, [options, selectedIcon, allPosesAreJpg])

  let bgUrl = ''
  if (currentOrderPackage?.background?.imageUrl) {
    bgUrl = currentOrderPackage.background.imageUrl
  } else if (background?.imageUrl) {
    bgUrl = background.imageUrl
  } else if (typeof background === 'string') {
    bgUrl = background
  }

  console.log('Final bgUrl:', bgUrl);

  let studentImageUrl
  if (foreground) {
    studentImageUrl = foreground
  } else if (currentOrderPackage && currentOrderPackage.studentImage) {
    studentImageUrl = currentOrderPackage.studentImage.watermarkUrl
  } else if (currentPackage && !currentPackage.placeholder.includes('placeholder.png')) {
    studentImageUrl = currentPackage.placeholder
  } else {
    studentImageUrl = placeholder
  }

  if (!currentPackage) {
    return (
      <div className={styles.spinnerContainer}>
        <Spinner color="blue" />
      </div>
    )
  }

  if (favoritesIds && favoritesIds.length > 0 && poseLoading) {
    console.log('Showing spinner because poseLoading is true');
    return (
      <div className={classes.spinnerContainer}>
        <Spinner color="blue" />
      </div>
    )
  }

  // "Prints / Digital Files" is no longer an in-page builder tab: the NavBar button routes
  // to the Photo Gifts & Add Ons page (routes.holiday.builder). pid = the current package;
  // no order-package is required (the add-ons page creates one when something is added).
  const goToAddons = () => history.push(routes.holiday.builder({ optional: true, pid: packageId }));

  // Optional "No thanks" affordance — only rendered when an onSkip callback is
  // passed (senior upsell step). Lets a senior proceed with just their $0 yearbook
  // line. Default null → elementary/undergrad render nothing here.
  const renderSkip = () => {
    if (!onSkip) return null;

    return (
      <div style={{ textAlign: 'center', margin: '1.5em 0 3em' }}>
        <button
          type="button"
          onClick={onSkip}
          style={{
            background: 'none', border: 'none', color: '#3883c0',
            cursor: 'pointer', fontSize: '1.3em', fontWeight: 'bold',
          }}
        >
          No thanks, just my yearbook photo now
        </button>
      </div>
    );
  };

  // "Use new package layout?" and "?ui=v2" are independent of the older
  // "Use new package builder?" toggle -- the two admin flags control
  // totally separate things. Enter this render block for any of the three
  // so `useNewLayout` alone (or the preview URL flag alone) is enough to
  // switch a pricelist onto the redesigned cards, even when the package
  // is still on the legacy new-package-builder off setting.
  if (currentPackage && (
    currentPackage.useNewPackageBuilder
    || currentPackage.useNewLayout
    || ui === 'v2'
  )) {
    return (
      <>
        <ToastContainer position="bottom-left" />

        <NavBar
          selectedIcon={selectedIcon}
          setSelectedIcon={setSelectedIcon}
          showPrints={!!giftPricelist}
          onPrints={goToAddons}
        />

        <div className={styles.packageContainer}>
          {heading && (
            <div className={styles.heading}>
              <Typography align="center" weight="bold">{heading}</Typography>
            </div>
          )}
          <PromoBanner offer={offer} className="mt-2" />
          {(ui === 'v2' || currentPackage?.useNewLayout) ? (
            // Redesigned layout. Triggered per-pricelist by the admin's
            // "Use new package layout?" checkbox on the Package edit page
            // (packages.use_new_layout). The `?ui=v2` URL flag stays as a
            // manual preview override so a non-flagged package can still
            // be previewed on staging/localhost without an admin edit.
            //
            // Options with an uploaded thumbnail become featured cards in
            // a 3-across grid (with image + full details). Options with no
            // thumbnail (or with the admin's "Other Packages" checkbox
            // ticked) drop into an "Other packages" section below as
            // text-only compact cards.
            (() => {
              // Two ways an Option ends up in the "Other packages" section:
              //   1. It has no thumbnail (imageUrl missing / placeholder) --
              //      historical default. Uploading a thumbnail in admin
              //      promotes it back to a featured card.
              //   2. Admin explicitly ticks the "Other Packages" checkbox
              //      on the option (options.in_other_packages). Lets an
              //      Option with a real thumbnail stay in Other packages
              //      without having to remove the artwork.
              const isFeatured = (opt) => (
                opt.imageUrl
                && !opt.imageUrl.includes('missing')
                && !opt.inOtherPackages
              )
              const featured = collection.filter(isFeatured)
              const extras = collection.filter((opt) => !isFeatured(opt))

              // Reorder featured so the Most Popular card sits at index 1
              // (middle of a 3-across desktop row). If no card is marked
              // Most Popular the order is untouched.
              const popularIdx = featured.findIndex((opt) => opt.mostPopular)
              if (popularIdx > 1) {
                const [popular] = featured.splice(popularIdx, 1)
                featured.splice(1, 0, popular)
              } else if (popularIdx === 0 && featured.length >= 2) {
                // If Most Popular was first in the array, swap it into the
                // middle slot instead.
                const [popular] = featured.splice(0, 1)
                featured.splice(1, 0, popular)
              }
              const commonProps = (option) => ({
                orderPackage,
                backgroundUrl: bgUrl,
                studentImageUrl,
                multiPoseHandler: multiPoseHandler ? (id) => multiPoseHandler(options.find((opt) => opt.id === id)) : null,
                option,
                price: option.prices[0],
                imageTypes: option.imageTypes,
                currentOrderPackage,
                packageId,
                accessCodeOnly,
                selectedPose,
                preorderPose,
                isElementary,
                offer,
              })
              return (
                <>
                  {featured.length > 0 && (
                    <div className={styles.v2Grid}>
                      {featured.map((option) => (
                        <PackageDisplayV2 key={option.id} {...commonProps(option)} />
                      ))}
                    </div>
                  )}
                  {extras.length > 0 && (
                    <>
                      <h2 className={styles.v2ExtrasHeading}>Other packages</h2>
                      <div className={styles.v2ExtrasRow}>
                        {extras.map((option) => (
                          <PackageDisplayV2 key={option.id} {...commonProps(option)} compact />
                        ))}
                      </div>
                    </>
                  )}
                </>
              )
            })()
          ) : (
            collection.map((option) => (
              <PackageDisplay
                orderPackage={orderPackage}
                backgroundUrl={bgUrl}
                studentImageUrl={studentImageUrl}
                key={option.id}
                multiPoseHandler={multiPoseHandler ? (id) => multiPoseHandler(options.find((opt) => opt.id === id)) : null}
                option={option}
                price={option.prices[0]}
                imageTypes={option.imageTypes}
                currentOrderPackage={currentOrderPackage}
                packageId={packageId}
                accessCodeOnly={accessCodeOnly}
                selectedPose={selectedPose}
                preorderPose={preorderPose}
                isElementary={isElementary}
                offer={offer}
              />
            ))
          )}
        </div>
        {renderSkip()}
      </>
    )
  }

  // Filter options for the old display if all poses are JPGs
  const displayOptions = allPosesAreJpg 
    ? options.filter(option => 
        option.name && (
          option.name.includes('8x10') || 
          option.name.includes('Team Picture')
        )
      )
    : options;

  console.log('Display options:', displayOptions);

  return (
    <>
      <ToastContainer position="bottom-left" />
      {displayOptions.map((option) => (
        <OptionDisplay
          backgroundUrl={bgUrl}
          studentImageUrl={studentImageUrl}
          key={option.id}
          multiPoseHandler={multiPoseHandler ? (id) => multiPoseHandler(options.find((opt) => opt.id === id)) : null}
          option={option}
          price={option.prices[0]}
          imageTypes={option.imageTypes}
          currentOrderPackage={currentOrderPackage}
          packageId={packageId}
          accessCodeOnly={accessCodeOnly}
        />
      ))}
      {renderSkip()}
    </>
  )
}

OptionSelectionContent.propTypes = {
  background: PropTypes.string,
  foreground: PropTypes.string,
  multiPoseHandler: PropTypes.func,
  packageId: PropTypes.string.isRequired,
  hideYearbookOption: PropTypes.bool,
  onSkip: PropTypes.func,
}

OptionSelectionContent.defaultProps = {
  background: null,
  foreground: null,
  multiPoseHandler: null,
  hideYearbookOption: false,
  onSkip: null,
}

export default OptionSelectionContent
